import {
  formDialogBodyStyles, formDialogCancelStyles, formDialogCloseStyles, formDialogDangerStyles, formDialogFooterStyles,
  formDialogHeaderStyles, formDialogMarkStyles, formDialogPaperStyles, formDialogTextStyles, formDialogTitleStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { red } from '@theme/primitives';
import { Trash2, XIcon } from 'lucide-react';
import { useId, useState } from 'react';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  warningSubtitle?: string;
}

/** Asks before something is deleted, in the look of the record dialogs. */
const DeleteConfirmationDialog = ({ open, onClose, onConfirm, warningSubtitle }: DeleteConfirmationDialogProps) => {
  const [isLoading, setLoading] = useState(false);
  const { t } = useLocalization();
  const titleId = useId();
  const textId = useId();

  const confirm = async () => {
    setLoading(true);

    await onConfirm();
    onClose();

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={!isLoading ? onClose : undefined}
      aria-labelledby={titleId}
      aria-describedby={textId}
      slotProps={{ paper: { sx: formDialogPaperStyles } }}
    >
      <Box sx={formDialogHeaderStyles}>
        {/* In the red of what it asks about, as the user dialog's mark is in blue. */}
        <Box sx={formDialogMarkStyles(red[300])}><Trash2 size={16} /></Box>
        <Typography id={titleId} component="h2" sx={formDialogTitleStyles}>
          {t('General_ConfirmDelete_Title')}
        </Typography>
        <IconButton aria-label={t('Common_Close')} onClick={onClose} disabled={isLoading} sx={formDialogCloseStyles}>
          <XIcon size={18} />
        </IconButton>
      </Box>
      <Box id={textId} sx={{ ...formDialogBodyStyles, pb: 2.5 }}>
        <Typography sx={formDialogTextStyles}>{t('General_ConfirmDelete_Content')}</Typography>
        {warningSubtitle && <Typography sx={{ ...formDialogTextStyles, mt: 1, color: red[300] }}>{warningSubtitle}</Typography>}
      </Box>
      <Box sx={formDialogFooterStyles}>
        <Button onClick={onClose} disabled={isLoading} sx={formDialogCancelStyles}>
          {t('Common_Cancel')}
        </Button>
        <Button onClick={confirm} loading={isLoading} autoFocus sx={formDialogDangerStyles}>
          {t('Common_Delete')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
